import { createFileRoute } from '@tanstack/react-router'
import {
  CalendarDays,
  CalendarX2,
  ChevronDown,
  CircleCheck,
  FileText,
  Link2Off,
  Loader2,
  Mail,
  MapPin,
  PenLine,
  Phone,
  RotateCcw,
  RotateCw,
  Send,
  SquarePen,
  Upload,
  WifiOff,
} from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Logo } from '@/components/brand/logo'
import { getJson, postJson, PublicError } from '@/components/proposal/client-api'
import { AcknowledgeTask, FormTask, SignTask, UploadTask } from '@/components/starter/task-forms'
import type { PublicPack, PublicTask } from '@/components/starter/types'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/** A new starter's pack. Public: access is their private link. */
export const Route = createFileRoute('/s/$token')({ component: StarterPackPage })

type Load =
  | { status: 'loading' }
  | { status: 'ready'; data: PublicPack }
  | { status: 'invalid' }
  | { status: 'expired' }
  | { status: 'error'; message: string }

const KIND_ICON = { sign: PenLine, form: SquarePen, acknowledge: FileText, upload: Upload }
const ukDate = (d: string | null) =>
  d
    ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(
        new Date(d.length === 10 ? `${d}T12:00:00Z` : d),
      )
    : ''

function StarterPackPage() {
  const { token } = Route.useParams()
  const [load, setLoad] = useState<Load>({ status: 'loading' })
  const [open, setOpen] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  useLightDocument()

  const fetchPack = useCallback(async () => {
    try {
      setLoad({
        status: 'ready',
        data: await getJson<PublicPack>(`/api/public/s/${encodeURIComponent(token)}`),
      })
    } catch (err) {
      if (err instanceof PublicError && err.status === 404) setLoad({ status: 'invalid' })
      else if (err instanceof PublicError && err.status === 410) setLoad({ status: 'expired' })
      else
        setLoad({
          status: 'error',
          message: err instanceof Error ? err.message : 'Something went wrong.',
        })
    }
  }, [token])

  useEffect(() => {
    void fetchPack()
  }, [fetchPack])

  useEffect(() => {
    document.title = 'Your starter pack · HCD Cleaning'
  }, [])

  const onDone = useCallback((p: PublicPack) => {
    setLoad({ status: 'ready', data: p })
    // Move on to the next thing to do.
    const next = p.tasks.find((t) => t.status !== 'done' && (t.required || t.status === 'rejected'))
    setOpen(next?.id ?? null)
    if (next)
      setTimeout(
        () =>
          document
            .getElementById(`task-${next.id}`)
            ?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
        50,
      )
  }, [])

  if (load.status === 'loading') return <PackSkeleton />
  if (load.status === 'invalid')
    return (
      <Notice icon={<Link2Off />} title="This link is not valid">
        Please check you have the full link from your email, or ask HCD Cleaning for a new one.
      </Notice>
    )
  if (load.status === 'expired')
    return (
      <Notice icon={<CalendarX2 />} title="This link has expired">
        Please ask HCD Cleaning to send you a new one. Anything you have already done is saved.
      </Notice>
    )
  if (load.status === 'error')
    return (
      <Notice icon={<WifiOff />} title="We could not load your starter pack">
        <p>{load.message}</p>
        <Button className="mt-5 h-11" onClick={() => void fetchPack()}>
          <RotateCw />
          Try again
        </Button>
      </Notice>
    )

  const { data } = load
  const s = data.starter
  const p = data.progress
  const submitted = s.status === 'submitted' && p.rejected === 0
  const pct = p.required ? Math.round((p.requiredDone / p.required) * 100) : 100

  async function submit() {
    setSending(true)
    setSendError(null)
    try {
      setLoad({
        status: 'ready',
        data: await postJson<PublicPack>(`/api/public/s/${token}/submit`, {}),
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="min-h-dvh bg-background">
      <header className="bg-ink text-white">
        <div className="mx-auto max-w-2xl px-4 pt-6 pb-8 sm:px-6">
          <div className="inline-flex rounded-lg bg-white px-3 py-2">
            <Logo className="h-8" />
          </div>
          <p className="mt-8 text-sm font-semibold tracking-wide text-rinse uppercase">
            Starter pack
          </p>
          <h1 className="mt-1 text-3xl font-semibold sm:text-4xl" style={{ fontStretch: '86%' }}>
            Welcome to {data.company.name}, {s.firstName}
          </h1>
          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-white/80">
            {s.jobTitle && <span>{s.jobTitle}</span>}
            {s.startDate && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="size-4" aria-hidden />
                Starting {ukDate(s.startDate)}
              </span>
            )}
            {s.site && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="size-4" aria-hidden />
                {s.site}
              </span>
            )}
          </p>
          <div className="mt-6">
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={p.required}
              aria-valuenow={p.requiredDone}
              aria-label={`${p.requiredDone} of ${p.required} required items done`}
              className="h-2.5 overflow-hidden rounded-full bg-white/15"
            >
              <div className="h-full rounded-full bg-rinse" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-sm text-white/80">
              {p.requiredDone} of {p.required} required items done
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-2xl gap-4 px-4 py-6 pb-32 sm:px-6">
        {submitted ? (
          <section
            className="flex items-start gap-4 rounded-2xl border border-success/40 bg-success/5 p-5"
            role="status"
          >
            <CircleCheck className="size-7 shrink-0 text-success" aria-hidden />
            <div>
              <h2 className="text-xl font-semibold">Thank you, your pack has been sent</h2>
              <p className="mt-1 text-[15px] text-muted-foreground">
                HR will check it and be in touch if anything else is needed. You can close this
                page.
              </p>
            </div>
          </section>
        ) : (
          <p className="text-[15px] text-muted-foreground">
            Work through each item below. Your progress saves as you go, so you can stop and come
            back to this link at any time before {ukDate(s.expiresAt)}.
          </p>
        )}

        <ol className="grid gap-3">
          {data.tasks.map((t, i) => (
            <TaskCard
              key={t.id}
              n={i + 1}
              task={t}
              token={token}
              firstName={s.firstName}
              open={open === t.id}
              locked={submitted}
              onToggle={() => setOpen((o) => (o === t.id ? null : t.id))}
              onDone={onDone}
            />
          ))}
        </ol>

        <ContactCard company={data.company} />
      </main>

      {!submitted && (
        <div className="fixed inset-x-0 bottom-0 border-t bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
          <div className="mx-auto grid max-w-2xl gap-2 px-4 py-3 sm:px-6">
            {sendError && (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{sendError}</AlertDescription>
              </Alert>
            )}
            <div className="flex items-center gap-3">
              <p className="flex-1 text-sm text-muted-foreground">
                {data.canSubmit
                  ? 'Everything required is done.'
                  : `${p.required - p.requiredDone + p.rejected} required ${p.required - p.requiredDone + p.rejected === 1 ? 'item' : 'items'} left`}
              </p>
              <Button
                className="h-12 px-6 text-base"
                disabled={!data.canSubmit || sending}
                onClick={() => void submit()}
              >
                {sending ? <Loader2 className="animate-spin" /> : <Send />}
                Send my pack
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function TaskCard({
  n,
  task: t,
  token,
  firstName,
  open,
  locked,
  onToggle,
  onDone,
}: {
  n: number
  task: PublicTask
  token: string
  firstName: string
  open: boolean
  locked: boolean
  onToggle: () => void
  onDone: (p: PublicPack) => void
}) {
  const Icon = KIND_ICON[t.kind]
  const done = t.status === 'done'
  const returned = t.status === 'rejected'
  const editable = !locked && !t.checked && !(t.kind === 'sign' && done)
  const status = t.checked
    ? 'Checked by HCD'
    : returned
      ? 'Needs changing'
      : done
        ? 'Done'
        : t.required
          ? 'To do'
          : 'Optional'
  const panelId = `task-${t.id}-panel`
  return (
    <li
      id={`task-${t.id}`}
      data-kind={t.kind}
      data-form={t.form ?? ''}
      data-status={t.status}
      data-required={t.required ? 'yes' : 'no'}
      className={cn(
        'scroll-mt-4 rounded-2xl border bg-card',
        returned && 'border-destructive/40',
        done && !returned && 'border-success/30',
      )}
    >
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
          className="flex w-full items-center gap-3 rounded-2xl px-4 py-4 text-left"
        >
          <span
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
              done && !returned
                ? 'bg-success text-white'
                : returned
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-accent text-accent-foreground',
            )}
            aria-hidden
          >
            {done && !returned ? <CircleCheck className="size-5" /> : n}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold">{t.name}</span>
            <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
              <Icon className="size-3.5" aria-hidden />
              {status}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'size-5 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </h2>
      {open && (
        <div id={panelId} className="grid gap-4 border-t px-4 py-4">
          {t.description && <p className="text-[15px] whitespace-pre-line">{t.description}</p>}
          {returned && t.returnedNote && (
            <Alert role="status" className="border-destructive/40">
              <RotateCcw />
              <AlertDescription>
                <strong>HCD asked you to change this:</strong> {t.returnedNote}
              </AlertDescription>
            </Alert>
          )}
          {!editable ? (
            <p className="text-[15px] text-muted-foreground">
              {t.checked
                ? 'HCD has checked this. Nothing more to do.'
                : t.kind === 'sign' && t.signedAs
                  ? `Signed as ${t.signedAs}.`
                  : 'Done.'}
            </p>
          ) : t.kind === 'acknowledge' ? (
            done ? (
              <p className="text-[15px] text-muted-foreground">You confirmed you have read this.</p>
            ) : (
              <AcknowledgeTask token={token} task={t} onDone={onDone} />
            )
          ) : t.kind === 'form' ? (
            <FormTask token={token} task={t} onDone={onDone} />
          ) : t.kind === 'sign' ? (
            <SignTask token={token} task={t} firstName={firstName} onDone={onDone} />
          ) : (
            <UploadTask token={token} task={t} onDone={onDone} />
          )}
        </div>
      )}
    </li>
  )
}

function ContactCard({ company }: { company: PublicPack['company'] }) {
  if (!company.phone && !company.email) return null
  return (
    <section className="mt-2 rounded-2xl border bg-card p-4">
      <h2 className="font-semibold">Questions?</h2>
      <p className="mt-1 text-[15px] text-muted-foreground">
        Get in touch with {company.name} and we will help.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {company.phone && (
          <a
            href={`tel:${company.phone.replace(/\s/g, '')}`}
            className="inline-flex h-11 items-center gap-2 rounded-md border px-4 text-sm font-medium hover:bg-secondary"
          >
            <Phone className="size-4" aria-hidden />
            {company.phone}
          </a>
        )}
        {company.email && (
          <a
            href={`mailto:${company.email}`}
            className="inline-flex h-11 items-center gap-2 rounded-md border px-4 text-sm font-medium hover:bg-secondary"
          >
            <Mail className="size-4" aria-hidden />
            {company.email}
          </a>
        )}
      </div>
    </section>
  )
}

function Notice({
  icon,
  title,
  children,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
}) {
  return (
    <main className="grid min-h-dvh place-items-center bg-background px-5 py-10">
      <div className="max-w-md text-center">
        <Logo className="mx-auto mb-8 h-12" />
        <span
          className="mx-auto flex size-12 items-center justify-center rounded-full bg-accent text-accent-foreground [&_svg]:size-6"
          aria-hidden
        >
          {icon}
        </span>
        <h1 className="mt-4 text-2xl font-semibold">{title}</h1>
        <div className="mt-2 text-[15px] text-muted-foreground">{children}</div>
      </div>
    </main>
  )
}

function PackSkeleton() {
  return (
    <div
      className="min-h-dvh bg-background"
      aria-busy="true"
      aria-label="Loading your starter pack"
    >
      <div className="bg-ink">
        <div className="mx-auto grid max-w-2xl gap-4 px-4 pt-6 pb-8 sm:px-6">
          <Skeleton className="h-12 w-36 bg-white/15" />
          <Skeleton className="mt-8 h-10 w-4/5 bg-white/15" />
          <Skeleton className="h-3 w-full bg-white/10" />
        </div>
      </div>
      <div className="mx-auto grid max-w-2xl gap-3 px-4 py-6 sm:px-6">
        <Skeleton className="h-18" />
        <Skeleton className="h-18" />
        <Skeleton className="h-18" />
      </div>
    </div>
  )
}

/** Starter pages always render in the light theme, like client documents. */
function useLightDocument() {
  useEffect(() => {
    const root = document.documentElement
    const force = () => {
      if (root.classList.contains('dark')) root.classList.remove('dark')
      root.style.colorScheme = 'light'
    }
    force()
    const obs = new MutationObserver(force)
    obs.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])
}
